'use client';

import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { AutoSelectContextProvider } from '@/new-cars/AutoSelect';
import ConfiguratorNavigation from '@/new-cars/ConfiguratorNavigation';
import DoorsSection from '@/new-cars/DoorsSection';
import { ParentModelHero } from '@/new-cars/Hero';
import GeolocationModal from '@/new-cars/Modals/GeolocationModal';
import ModelVersionSection from '@/new-cars/ModelVersionSection';
import OptionsSection from '@/new-cars/OptionsSection';
import PackagesSection from '@/new-cars/PackagesSection';
import TrimSection from '@/new-cars/TrimSection';
import TruckBodyStyleSection from '@/new-cars/TruckBodyStyleSection';
import YearSection from '@/new-cars/YearSection';
import CustomConfigs from '~/constants/customConfigs';
import SectionName from '~/constants/new-cars/SectionName';
import { useConfiguration } from '~/context/common/StaticContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import useCubeSelectionSync from '~/hooks/new-cars/useCubeSelectionSync';
import useRecordNewCarsFlowVisit from '~/hooks/new-cars/useRecordNewCarsFlowVisit';
import useSectionAnimation from '~/hooks/new-cars/useSectionAnimation';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import {
  NEW_CARS_FLOW_LEVELS,
  type NewCarsFlowLevel
} from '~/utilities/new-cars/newCarsFlowLevel';
import { SectionWrapper } from './ParentModelContainer.styles';
import StickyFooter from './StickyFooter/StickyFooter';
import TdcHeader from './Tdc/TdcHeader';
import TdcPairingScreen from './Tdc/TdcPairingScreen';
import TdcSentScreen from './Tdc/TdcSentScreen';

type OwnProps = {
  highestLevelVisited: NewCarsFlowLevel | null;
  showHero: boolean;
};

export default function ParentModelContainer({
  highestLevelVisited,
  showHero
}: OwnProps) {
  const {
    customizeOptions,
    isFirstSection,
    isSectionIncluded,
    selectedOptions,
    selectedParentModel,
    selectedTrim,
    selectedTruckBodyStyle,
    trims
  } = useContext(ParentModelContext);
  const { enableNewCarConfiguratorV2 } = useConfiguration();
  useRecordNewCarsFlowVisit(NEW_CARS_FLOW_LEVELS.parentModel);

  // Clear the transition cookie set by ParentModelCard on click. It suppresses
  // the header server-side for the RSC fetch and must not persist to other pages.
  useEffect(() => {
    document.cookie = `${CustomConfigs.RemoveHeader}=; path=/; max-age=0`;
  }, []);

  const hasModelFirst =
    isSectionIncluded(SectionName.ModelVersion) ||
    isSectionIncluded(SectionName.Doors);
  const hasTruckBodyStyle = isSectionIncluded(SectionName.TruckBodyStyle);
  const hasYearFirst = isSectionIncluded(SectionName.Year);
  const { parentModel } = selectedParentModel;
  const isFirstVisit =
    highestLevelVisited === null ||
    highestLevelVisited === NEW_CARS_FLOW_LEVELS.parentModel;

  let modelOrDoorsHeading: string | undefined;
  let optionsHeading: string | undefined;
  let trimHeading: string | undefined;
  let truckBodyStyleHeading: string | undefined;
  let yearHeading: string | undefined;

  if (isFirstVisit) {
    if (hasYearFirst) {
      yearHeading = 'Get started by selecting a year';
      trimHeading = 'Now pick your preferred trim';
      optionsHeading = 'Finally, select your favorite colors and options';
    } else if (hasModelFirst) {
      modelOrDoorsHeading = 'Get started by selecting a model';
      trimHeading = 'Now pick your preferred trim';
      optionsHeading = 'Finally, select your favorite colors and options';
    } else if (hasTruckBodyStyle) {
      trimHeading = 'Get started by selecting a trim';
      truckBodyStyleHeading = 'Now pick your preferred cab/box type';
      optionsHeading = 'Finally, select your favorite colors and options';
    } else {
      trimHeading = 'Get started by selecting a trim';
      optionsHeading = 'Now select your favorite colors and options';
    }
  } else if (hasYearFirst) {
    yearHeading = 'Select a year';
  } else if (hasModelFirst) {
    modelOrDoorsHeading = parentModel
      ? `Select your ${parentModel}`
      : undefined;
  } else {
    trimHeading = 'Select your preferred trim';
  }
  const testDriveCenter = useTestDriveCenter();
  const { sendCube } = testDriveCenter;

  // The configurator page is the trim/options stage of the flow. Tell the
  // paired cube display to show its "trim" screen for this make/model when we
  // mount here (no-op when not paired). Reads make/model from the parent-model
  // context so we don't need to thread the session/selection through props.
  useEffect(() => {
    if (testDriveCenter.isSent || testDriveCenter.isPairing) return;
    const make = selectedParentModel.make || customizeOptions.data.make;
    const model = selectedParentModel.model || customizeOptions.data.model;
    if (!make && !model) return;
    sendCube({ screen: 'trim', make, model });
  }, [
    sendCube,
    testDriveCenter.isSent,
    testDriveCenter.isPairing,
    selectedParentModel.make,
    selectedParentModel.model,
    customizeOptions.data.make,
    customizeOptions.data.model
  ]);

  // Once a trim is selected, the customer moves on to the options stage. The cube
  // isn't synced to those option choices — it just loops the selected vehicle's
  // lifestyle imagery until the build (tour) is sent. Fire the trim-select signal
  // that kicks off that autonomous loop on the paired display.
  useEffect(() => {
    if (testDriveCenter.isSent || testDriveCenter.isPairing) return;
    if (!selectedTrim) return;
    const make = selectedParentModel.make || customizeOptions.data.make;
    const model = selectedParentModel.model || customizeOptions.data.model;
    if (!make && !model) return;
    sendCube({ screen: 'lifestyle', make, model, trim: selectedTrim.trim });
  }, [
    sendCube,
    testDriveCenter.isSent,
    testDriveCenter.isPairing,
    selectedTrim,
    selectedParentModel.make,
    selectedParentModel.model,
    customizeOptions.data.make,
    customizeOptions.data.model
  ]);

  // Stream each options-stage selection (color, drivetrain, engine, packages…)
  // to the paired cube display so it can show the live build as the customer
  // configures.
  useCubeSelectionSync(testDriveCenter);

  const { activeSection, heroRef, sectionRefs } = useSectionAnimation();
  const initialSection = useRef(activeSection);
  const [AutoSelectResetKey, setAutoSelectResetKey] = useState(0);

  const getInitialVisibility = (section: SectionName) =>
    initialSection.current !== section ? 'hidden' : undefined;

  useSubsequentEffect(() => {
    if (selectedTrim != null || selectedTruckBodyStyle != null) {
      setAutoSelectResetKey(Date.now());
    }
  }, [selectedTrim, selectedTruckBodyStyle]);

  if (testDriveCenter.isPairing) {
    return <TdcPairingScreen />;
  }

  if (testDriveCenter.isSent) {
    const data = customizeOptions.data;

    return (
      <TdcSentScreen
        buildSummary={{
          make: data.make || selectedOptions?.make || '',
          model: data.parentModel || selectedOptions?.parentModel || '',
          year: data.year || selectedOptions?.year || 0,
          trim: data.trim || selectedOptions?.trim || '',
          color: data.closestVehicle?.exteriorColor?.name,
          interior: data.closestVehicle?.interiorColor?.name
        }}
        tourData={testDriveCenter.tourData as any}
        onStartOver={testDriveCenter.startOver}
        onEndSession={testDriveCenter.endSession}
      />
    );
  }

  // V2 renders on every section (waiting for trims only when Trim is first); V1 keeps the section gate.
  const showStickyFooter = enableNewCarConfiguratorV2
    ? !isFirstSection(SectionName.Trim) || trims.isLoaded
    : trims.isLoaded &&
      (activeSection === SectionName.Options ||
        activeSection === SectionName.Trim ||
        activeSection === SectionName.TruckBodyStyle);

  return (
    <>
      <div className="mx-auto flex min-h-screen max-w-[1800px] flex-col bg-background-weak">
        {testDriveCenter.isActive && <TdcHeader />}
        {showHero && (
          <div className="relative z-9" ref={heroRef}>
            <ParentModelHero
              make={selectedParentModel.make}
              parentModel={selectedParentModel.parentModel}
            />
          </div>
        )}
        <div className="flex flex-1 flex-col md:flex-row md:gap-4xl md:px-4xl lg:max-w-[1200px] xl:max-w-[1272px] lg:mx-auto ">
          <ConfiguratorNavigation />
          <div className="flex-1">
            {isSectionIncluded(SectionName.Year) && (
              <div
                className={getInitialVisibility(SectionName.Year)}
                ref={sectionRefs.year}
              >
                <SectionWrapper>
                  <YearSection heading={yearHeading} />
                </SectionWrapper>
              </div>
            )}
            {isSectionIncluded(SectionName.ModelVersion) && (
              <div
                className={getInitialVisibility(SectionName.ModelVersion)}
                ref={sectionRefs.modelVersion}
              >
                <SectionWrapper>
                  <ModelVersionSection heading={modelOrDoorsHeading} />
                </SectionWrapper>
              </div>
            )}
            {isSectionIncluded(SectionName.Doors) && (
              <div
                className={getInitialVisibility(SectionName.Doors)}
                ref={sectionRefs.doors}
              >
                <SectionWrapper>
                  <DoorsSection heading={modelOrDoorsHeading} />
                </SectionWrapper>
              </div>
            )}
            <SectionWrapper
              className={getInitialVisibility(SectionName.Trim)}
              ref={sectionRefs.trim}
            >
              <TrimSection heading={trimHeading} />
            </SectionWrapper>
            {isSectionIncluded(SectionName.TruckBodyStyle) && (
              <SectionWrapper
                className={getInitialVisibility(SectionName.TruckBodyStyle)}
                ref={sectionRefs.truckBodyStyle}
              >
                <TruckBodyStyleSection heading={truckBodyStyleHeading} />
              </SectionWrapper>
            )}
            <SectionWrapper
              className={getInitialVisibility(SectionName.Options)}
              ref={sectionRefs.options}
            >
              <AutoSelectContextProvider key={AutoSelectResetKey}>
                <OptionsSection heading={optionsHeading} />
                <PackagesSection />
              </AutoSelectContextProvider>
            </SectionWrapper>
          </div>
        </div>
        {showStickyFooter && <StickyFooter />}
      </div>
      <GeolocationModal />
    </>
  );
}
